import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/hooks/useNotifications';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Bell, Loader2, Send, RefreshCw, AlertCircle } from 'lucide-react';
import { isServiceWorkerReady, getRegistration } from '@/lib/serviceWorkerRegistration';
import { supabase } from '@/integrations/supabase/client';

// Detect Mac/Safari
function isMac(): boolean {
  return /Mac|Macintosh/.test(navigator.userAgent) && !('ontouchend' in document);
}

function isSafari(): boolean {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

export function NotificationDiagnostics() {
  const { isAgent, isAdmin } = useAuth();
  const {
    isSupported,
    isEnabled,
    permission,
    needsIOSInstall,
    isDenied,
    isLoading,
    toggle,
    sendTestNotification,
  } = useNotifications();

  const handleToggle = async (enabled: boolean) => {
    const success = await toggle(enabled);
    if (success) {
      toast.success(enabled ? 'Notifications activées' : 'Notifications désactivées');
    } else if (enabled) {
      if (isDenied) {
        toast.error('Notifications bloquées par le navigateur');
      } else if (needsIOSInstall) {
        toast.error('Installez l\'app depuis Safari pour activer les notifications');
      } else {
        toast.error('Impossible d\'activer les notifications');
      }
    }
  };

  const handleTest = async () => {
    const success = await sendTestNotification();
    toast[success ? 'success' : 'error'](success ? 'Notification envoyée' : 'Échec de l\'envoi');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-between p-4 bg-card rounded-xl border border-border">
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-muted-foreground" />
          <span className="text-sm">Notifications</span>
        </div>
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isSupported) {
    return (
      <div className="p-4 bg-card rounded-xl border border-border">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Bell className="w-5 h-5" />
          <div>
            <p className="text-sm font-medium">Notifications non disponibles</p>
            <p className="text-xs">Votre navigateur ne supporte pas les notifications push.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Main toggle */}
      <div className="flex items-center justify-between p-4 bg-card rounded-xl border border-border">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isEnabled ? 'bg-primary/10' : 'bg-muted'}`}>
            <Bell className={`w-5 h-5 ${isEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>
          <div>
            <p className="text-sm font-medium">Notifications push</p>
            <p className="text-xs text-muted-foreground">
              {isEnabled ? 'Activées - vous recevez les alertes' : 'Désactivées'}
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={handleToggle}
          disabled={isLoading || isDenied || needsIOSInstall}
        />
      </div>

      {/* iOS install hint */}
      {needsIOSInstall && (
        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <p className="text-sm font-medium text-blue-600 mb-1">📱 Installation requise</p>
          <p className="text-xs text-blue-600/80">
            Pour activer les notifications sur iPhone/iPad, installez d'abord l'application :
          </p>
          <ol className="text-xs text-blue-600/80 mt-2 space-y-1 list-decimal list-inside">
            <li>Appuyez sur <strong>Partager</strong> (📤)</li>
            <li>Sélectionnez <strong>"Sur l'écran d'accueil"</strong></li>
            <li>Ouvrez l'app depuis l'écran d'accueil</li>
          </ol>
        </div>
      )}

      {/* Permission denied hint */}
      {isDenied && !needsIOSInstall && (
        <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <p className="text-sm font-medium text-amber-600 mb-1">🔒 Notifications bloquées</p>
          <p className="text-xs text-amber-600/80">
            Les notifications sont bloquées par votre navigateur. Pour les réactiver :
          </p>
          <ol className="text-xs text-amber-600/80 mt-2 space-y-1 list-decimal list-inside">
            <li>Cliquez sur l'icône 🔒 dans la barre d'adresse</li>
            <li>Trouvez "Notifications" et changez en "Autoriser"</li>
            <li>Rechargez la page</li>
          </ol>
        </div>
      )}

      {/* Mac/Safari specific hint */}
      {isMac() && isSafari() && !isEnabled && !isDenied && (
        <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
          <p className="text-sm font-medium text-purple-600 mb-1">🍎 Safari sur Mac</p>
          <p className="text-xs text-purple-600/80">
            Safari sur Mac supporte les notifications web. Assurez-vous que :
          </p>
          <ol className="text-xs text-purple-600/80 mt-2 space-y-1 list-decimal list-inside">
            <li>macOS 13+ (Ventura) et Safari 16+</li>
            <li>Notifications autorisées dans Préférences Système → Notifications → Safari</li>
            <li>Le site a la permission d'envoyer des notifications</li>
          </ol>
        </div>
      )}

      {/* Admin/Agent debug */}
      {(isAgent || isAdmin) && (
        <AdvancedDebugPanel 
          permission={permission} 
          isEnabled={isEnabled} 
          isSupported={isSupported}
          onSendTest={handleTest}
        />
      )}
    </div>
  );
}

function AdvancedDebugPanel({ 
  permission, 
  isEnabled, 
  isSupported, 
  onSendTest 
}: { 
  permission: string; 
  isEnabled: boolean; 
  isSupported: boolean;
  onSendTest: () => void;
}) {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [swStatus, setSwStatus] = useState<string>('Non vérifié');
  const [dbSubscription, setDbSubscription] = useState<string>('Non vérifié');
  const [oneSignalStatus, setOneSignalStatus] = useState<string>('Non vérifié');
  const [pushEndpoint, setPushEndpoint] = useState<string>('Non vérifié');

  // Check status on mount
  useEffect(() => {
    checkAllStatus();
  }, []);

  const checkAllStatus = async () => {
    setIsLoading(true);
    
    // Check Service Worker
    try {
      const swReady = await isServiceWorkerReady();
      const reg = await getRegistration();
      if (swReady && reg) {
        setSwStatus(`✓ Actif (scope: ${reg.scope})`);
      } else if (reg) {
        setSwStatus(`⏳ En attente (installing: ${!!reg.installing}, waiting: ${!!reg.waiting})`);
      } else {
        setSwStatus('✗ Non enregistré');
      }
    } catch (e) {
      setSwStatus(`✗ Erreur: ${e}`);
    }

    // Check Push Subscription
    try {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const endpointType = sub.endpoint.includes('push.apple.com') ? 'Safari/APNs' :
                               sub.endpoint.includes('fcm.googleapis.com') ? 'Chrome/FCM' :
                               sub.endpoint.includes('mozilla.com') ? 'Firefox' : 'Unknown';
          setPushEndpoint(`✓ ${endpointType}`);
        } else {
          setPushEndpoint('✗ Pas de subscription');
        }
      } else {
        setPushEndpoint('✗ Non supporté');
      }
    } catch (e) {
      setPushEndpoint(`✗ Erreur: ${e}`);
    }

    // Check DB Subscription
    if (user?.id) {
      try {
        const { data, error } = await supabase
          .from('push_subscriptions')
          .select('id, endpoint, updated_at')
          .eq('user_id', user.id);
        
        if (error) {
          setDbSubscription(`✗ Erreur DB: ${error.message}`);
        } else if (data && data.length > 0) {
          const types = data.map(s => {
            if (s.endpoint.includes('push.apple.com')) return 'APNs';
            if (s.endpoint.includes('fcm.googleapis.com')) return 'FCM';
            if (s.endpoint.includes('mozilla.com')) return 'Firefox';
            return '?';
          });
          setDbSubscription(`✓ ${data.length} sub(s): ${types.join(', ')}`);
        } else {
          setDbSubscription('✗ Aucune dans DB');
        }
      } catch (e) {
        setDbSubscription(`✗ Erreur: ${e}`);
      }
    }

    // Check OneSignal
    const onesignal = window.OneSignal;
    if (onesignal) {
      try {
        const isPushSupported = onesignal.Notifications?.isPushSupported?.();
        const perm = onesignal.Notifications?.permission;
        const userId = onesignal.User?.externalId || 'Non défini';
        setOneSignalStatus(`Push: ${isPushSupported ? '✓' : '✗'} | Perm: ${perm ? '✓' : '✗'} | User: ${userId}`);
      } catch (e) {
        setOneSignalStatus(`Erreur: ${e}`);
      }
    } else {
      setOneSignalStatus('SDK non chargé');
    }

    setIsLoading(false);
  };

  const requestOneSignalPermission = async () => {
    setIsLoading(true);
    
    const onesignal = window.OneSignal;
    if (!onesignal) {
      toast.error('SDK OneSignal non chargé');
      setIsLoading(false);
      return;
    }

    try {
      const result = await onesignal.Notifications.requestPermission();
      toast.success(`Permission OneSignal: ${result ? 'Accordée' : 'Refusée'}`);
      checkAllStatus();
    } catch (error) {
      console.error('[OneSignal Permission]', error);
      toast.error(`Erreur: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-muted-foreground hover:text-foreground py-2">
        🔧 Debug avancé (admin/agent)
      </summary>
      <div className="mt-2 p-3 bg-muted/30 rounded-lg space-y-3 font-mono text-muted-foreground">
        {/* Environment info */}
        <div className="space-y-1 border-b border-border pb-2">
          <p className="font-semibold text-foreground text-[11px]">Environnement</p>
          <p>Plateforme: <span className="text-foreground">{isMac() ? '🍎 Mac' : '💻 Autre'} {isSafari() ? '(Safari)' : ''}</span></p>
          <p>User Agent: <span className="text-foreground text-[9px] break-all">{navigator.userAgent.substring(0, 80)}...</span></p>
        </div>

        {/* Status */}
        <div className="space-y-1 border-b border-border pb-2">
          <p className="font-semibold text-foreground text-[11px]">Status</p>
          <p>Permission navigateur: <span className="text-foreground">{permission}</span></p>
          <p>Notifications UI: <span className="text-foreground">{isEnabled ? '✓ Activées' : '✗ Désactivées'}</span></p>
          <p>Push supporté: <span className="text-foreground">{isSupported ? '✓' : '✗'}</span></p>
        </div>

        {/* Technical details */}
        <div className="space-y-1 border-b border-border pb-2">
          <p className="font-semibold text-foreground text-[11px]">Détails techniques</p>
          <p>Service Worker: <span className="text-foreground text-[10px]">{swStatus}</span></p>
          <p>Push Subscription: <span className="text-foreground text-[10px]">{pushEndpoint}</span></p>
          <p>DB Subscription: <span className="text-foreground text-[10px]">{dbSubscription}</span></p>
          <p>OneSignal: <span className="text-foreground text-[10px]">{oneSignalStatus}</span></p>
        </div>

        {/* Error hints */}
        {swStatus.includes('Non enregistré') && (
          <div className="p-2 bg-red-500/10 border border-red-500/20 rounded text-red-600">
            <div className="flex items-start gap-1">
              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <p>Le Service Worker n'est pas enregistré. Cela peut être normal en mode preview. Les notifications fonctionneront en production.</p>
            </div>
          </div>
        )}

        {dbSubscription.includes('Aucune') && pushEndpoint.includes('✓') && (
          <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded text-amber-600">
            <div className="flex items-start gap-1">
              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <p>La subscription push existe dans le navigateur mais pas dans la DB. Essayez de désactiver puis réactiver les notifications.</p>
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button 
            size="sm" 
            variant="outline"
            onClick={checkAllStatus}
            disabled={isLoading}
            className="gap-1"
          >
            {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Actualiser
          </Button>
          
          <Button 
            size="sm" 
            variant="outline"
            onClick={requestOneSignalPermission}
            disabled={isLoading}
          >
            OneSignal Permission
          </Button>
          
          <Button 
            size="sm" 
            variant="default"
            onClick={onSendTest}
            className="gap-1"
          >
            <Send className="w-3 h-3" />
            Test push
          </Button>
        </div>
      </div>
    </details>
  );
}
